import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AppdataService } from 'src/app/services/app-data/appdata.service';
import { CommunicationInterfaceService } from 'src/app/services/communicationInterface/communication-interface.service';
import { LivechatStartService } from 'src/app/services/livechat-start/livechat-start.service';

@Component({
  selector: 'app-device-permissions',
  templateUrl: './device-permissions.component.html',
  styleUrls: ['./device-permissions.component.scss']
})
export class DevicePermissionsComponent implements OnInit {
  appLabels;
  permissions = {
    audio: false,
    video: false,
    location: false,
    webrtc: false,
    screenshare: false
  };
  actionsToRight: boolean;
  enabledPermissions = {};
  canProceed: boolean = false;
  proceedText: string = 'Proceed';
  appConfig: any;
  locationDisabled: boolean = false;
  proceedLoading: boolean = false;
  warningMessage: string = 'Please wait while checking access.....';

  constructor(
    private commInterface: CommunicationInterfaceService,
    public dialogRef: MatDialogRef<DevicePermissionsComponent>,
    private liveChatService: LivechatStartService,
    private appDataService: AppdataService,
    private cdr: ChangeDetectorRef
  ) {
    this.commInterface.logToServer(
      'info',
      'DevicePermissionsComponent.constructor',
      'DevicePermissionsComponent initiated'
    );
    this.appDataService?.labelObservable?.subscribe((updatedLabels: any) => {
      this.appLabels = updatedLabels;
      this.cdr.detectChanges();
    })
  }

  ngOnInit(): void {
    try {
      this.appConfig = this.appDataService.getConfig();
      this.appLabels = this.appDataService.getLabels();
      this.actionsToRight = this.appConfig.Miscellaneous.ButtonArrangement.actionsToRight;

      this.checkParallelCameraRestriction();

      this.commInterface.permissionsObservable.subscribe({
        next: (permission) => {
          setTimeout(() => {
            this.proceedText = permission.proceedText;
            this.permissions[permission.type] = permission.status;
            this.checkForMediaRestrictions();
          }, 1000);
        }
      });

      this.setEnabledPermissions();
      this.permissions = this.commInterface.devicePermissions;
      if (!this.appConfig.GeoLocation.Enable) {
        this.locationDisabled = true;
      }
      console.log(this.permissions);
      this.checkForMediaRestrictions();
    } catch (error) {
      console.error('%c Error Occured in ngOnInit', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'ngOnInit', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.ngOnInit',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to check paralell camera device access and
   * prevent the permission dialog from closing based on the config
   */
  checkParallelCameraRestriction(): void {
    try {
      if (!this.appConfig?.permissionsConfig?.allowToProceedWithoutMedia) {
        this.commInterface.getCameraParallelUsageStatus(this.commInterface);
        this.commInterface.parallelCameraUsageObservable.subscribe((status: any) => {
          if (status) {
            this.warningMessage = this.appLabels?.permissionsConfig?.parallelCameraUsageWarning;
            this.commInterface.logToServer(
              'info',
              'DevicePermissionsComponent.checkParallelCameraRestriction',
              this.warningMessage
            );
          } else {
            this.warningMessage = this.appLabels?.permissionsConfig?.warningMessageToProceed;
          }
          this.cdr.detectChanges();
        });
      } else {
        this.warningMessage = this.appLabels?.permissionsConfig?.warningMessageToProceed;
      }
    } catch (error) {
      console.error('%c Error Occured in checkParallelCameraRestriction', 'color: red', error);
      this.commInterface.sendUIErrors(
        'DevicePermissionsComponent',
        'checkParallelCameraRestriction',
        JSON.stringify(error)
      );
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.checkParallelCameraRestriction',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to set the permissions boolean value for enabled devices
   */
  setEnabledPermissions(): void {
    try {
      this.enabledPermissions = this.permissions;
      const permissionsOnChannel = this.appConfig.channelConfiguration[this.appConfig.channelConfiguration.chatmode];
      this.enabledPermissions['audio'] = permissionsOnChannel['audioEnabled'];
      this.enabledPermissions['video'] = permissionsOnChannel['videoEnabled'];
      this.enabledPermissions['location'] = this.appConfig.GeoLocation.Enable;
      this.enabledPermissions['screenshare'] = permissionsOnChannel['screenShareEnabled'];
      this.commInterface.logToServer(
        'info',
        'DevicePermissionsComponent.setEnabledPermissions',
        JSON.stringify(this.enabledPermissions)
      );
    } catch (error) {
      console.error('%c Error Occured in setEnabledPermissions', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'setEnabledPermissions', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.setEnabledPermissions',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to manually trigger permission checkups
   * @param permissionKey Type of permission
   * @param e click event
   */
  togglePermission(permissionKey, e?): void {
    try {
      e?.preventDefault();
      switch (permissionKey) {
        case 'location':
          this.liveChatService.getLocation();
          break;
        default:
          this.commInterface.getAudioVideoInputs();
          break;
      }
    } catch (error) {
      console.error('%c Error Occured in togglePermission', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'togglePermission', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.togglePermission',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to start auto proceeding permission dialog based on constrains and config
   */
  checkForMediaRestrictions(): void {
    try {
      if (
        (!this.appConfig?.permissionsConfig?.allowToProceedWithoutMedia &&
          (!this.permissions.audio || !this.permissions.video)) ||
        (this.appConfig.GeoLocation.Enable && this.appConfig.GeoLocation.Force && !this.permissions.location)
      ) {
        this.canProceed = false;
      } else {
        this.canProceed = true;
      }
      this.checkToAutoProceed();
    } catch (error) {
      console.error('%c Error Occured in checkForMediaRestrictions', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'checkForMediaRestrictions', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.checkForMediaRestrictions',
        JSON.stringify(error)
      );
    }
  }

  /**
   * Method to autoproceed i.e., closing the device permissions dialog box
   */
  checkToAutoProceed(): void {
    try {
      if (this.canProceed && this.appConfig?.permissionsConfig?.autoProceedPermissionsCheck) {
        this.proceedLoading = true;
        setTimeout(() => {
          this.proceedLoading = false;
          this.commInterface.canProceed = true;
          this.dialogRef.close();
          this.commInterface.logToServer('info', 'DevicePermissionsComponent.checkToAutoProceed', 'Auto-proceeded');
        }, 1500);
      }
    } catch (error) {
      console.error('%c Error Occured in checkToAutoProceed', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'checkToAutoProceed', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.checkToAutoProceed',
        JSON.stringify(error)
      );
    }
  }


  
  /**
   * Method to manually proceed i.e., closing the device permissions dialog box
   */
  checkToManualProceed(): void {
    try {
        this.canProceed = true;
        this.proceedLoading = true;
        setTimeout(() => {
          this.proceedLoading = false;
          this.commInterface.canProceed = true;
          this.dialogRef.close();
          this.commInterface.logToServer('info', 'DevicePermissionsComponent.checkToManualProceed', 'Auto-proceeded');
        }, 1500);
    } catch (error) {
      console.error('%c Error Occured in checkToManualProceed', 'color: red', error);
      this.commInterface.sendUIErrors('DevicePermissionsComponent', 'checkToManualProceed', JSON.stringify(error));
      this.commInterface.logToServer(
        'error',
        'Error occured in DevicePermissionsComponent.checkToManualProceed',
        JSON.stringify(error)
      );
    }
  }

}
